import React, { useEffect } from 'react'
import { PageTitle } from '../../uiComponents/typography'
import { SigmaService, EmbedUrlParams } from './sigmaService'
import { usePageHeight, useSigmaIframe } from '@sigmacomputing/react-embed-sdk'
import { getPreferredLanguage, LanguageSelector } from 'uiComponents/languageSelector/languageSelector'
import { useAppSelector } from 'store/hooks'
import { getPartnerCountryCode } from 'auth/selectors'
import { usePermission } from 'admin/hocs'
import PermissionRequired from 'admin/permissionRequired'
import { useLocation } from 'react-router-dom'
import { parseHashRoute } from 'utils/hash'
import './sigmaReport.scss'

interface SigmaReportProps {
    accountSlug: string
    sigmaService: SigmaService
    reportId: string
    title: string
}

// Keep in sync with the same breakpoint in sigmaReport.scss (theme.breakPoints.md).
const NARROW_VIEWPORT_QUERY = '(max-width: 900px)'
// Breathing room under the iframe so the report does not sit flush with the viewport edge.
const VIEWPORT_BOTTOM_GAP = 16
const MIN_NARROW_HEIGHT = 320

// Sigma renders its own overlays (the date filter list, modals) against the
// viewport of the iframe it lives in. With responsive_height the iframe is as
// tall as the entire report, so on a phone those overlays open far below the
// fold: the user sees the grey backdrop and never the list. On narrow screens
// we therefore drop responsive height and pin the iframe to the visible
// viewport, which lets the report scroll inside the frame and keeps its
// overlays on screen.
const useNarrowViewport = () => {
    const [narrow, setNarrow] = React.useState(
        () => typeof window !== 'undefined' && window.matchMedia?.(NARROW_VIEWPORT_QUERY).matches === true,
    )

    useEffect(() => {
        if (typeof window.matchMedia !== 'function') {
            return
        }
        const query = window.matchMedia(NARROW_VIEWPORT_QUERY)
        const onChange = (event: MediaQueryListEvent) => setNarrow(event.matches)
        setNarrow(query.matches)
        query.addEventListener('change', onChange)
        return () => query.removeEventListener('change', onChange)
    }, [])

    return narrow
}

export const SigmaReport = ({ accountSlug, sigmaService, reportId, title }: SigmaReportProps) => {
    const { iframeRef } = useSigmaIframe()
    const responsiveHeight = Math.ceil(usePageHeight(iframeRef) || 0)
    const [embedUrl, setEmbedUrl] = React.useState('')
    const { hasPermission } = usePermission()
    const hasAnalyticsPermission = hasPermission('access_analytics', accountSlug)
    const countryCode = useAppSelector(getPartnerCountryCode)
    const narrowViewport = useNarrowViewport()
    const [viewportFitHeight, setViewportFitHeight] = React.useState(0)
    // Sigma page/element nodeId deep link, e.g. /reports/sales#uywwmBHTxq.
    // Read from the router location so hash-only navigation (palette deep
    // links between report pages) re-renders and refetches the embed. The
    // hash is kept in the URL so refresh and history reopen the same page.
    const location = useLocation()
    const nodeId = parseHashRoute(location.hash)

    const getUrlParams = (): EmbedUrlParams => ({
        ...(nodeId ? { nodeId } : {}),
        responsive_height: !narrowViewport,
        menu_position: 'top',
        lng: countryCode === 'FR' ? getPreferredLanguage() : countryCode === 'US' ? 'en' : 'en-gb',
    })

    // Sequence requests: rapid hash navigation fires overlapping fetches, and
    // an out-of-order response must not overwrite the newest embed.
    const requestIdRef = React.useRef(0)
    const fetchEmbedUrl = (urlParams: EmbedUrlParams) => {
        const requestId = ++requestIdRef.current
        sigmaService.getEmbedUrl(accountSlug, reportId, urlParams).then((url) => {
            if (requestId === requestIdRef.current) {
                setEmbedUrl(url)
            }
        })
    }

    useEffect(() => {
        if (!embedUrl && hasAnalyticsPermission) {
            fetchEmbedUrl(getUrlParams())
        }
    })

    useEffect(() => {
        setEmbedUrl('')
    }, [hasAnalyticsPermission, accountSlug, reportId, countryCode, nodeId, narrowViewport])

    // Measure how much of the viewport is left below the iframe's top edge, so
    // the frame ends where the screen ends instead of running off the bottom.
    React.useLayoutEffect(() => {
        if (!narrowViewport || !embedUrl) {
            setViewportFitHeight(0)
            return
        }
        const measure = () => {
            // Clamp to 0: once the page is scrolled past the iframe's top edge
            // (Safari fires resize when its toolbar collapses mid-scroll) the
            // rect top is negative and would inflate the frame past the screen.
            const top = Math.max(0, iframeRef.current?.getBoundingClientRect().top ?? 0)
            const available = (window.visualViewport?.height ?? window.innerHeight) - top - VIEWPORT_BOTTOM_GAP
            setViewportFitHeight(Math.max(MIN_NARROW_HEIGHT, Math.round(available)))
        }
        measure()
        window.addEventListener('resize', measure)
        window.addEventListener('orientationchange', measure)
        window.visualViewport?.addEventListener('resize', measure)
        return () => {
            window.removeEventListener('resize', measure)
            window.removeEventListener('orientationchange', measure)
            window.visualViewport?.removeEventListener('resize', measure)
        }
    }, [narrowViewport, embedUrl, iframeRef])

    const iframeHeight = narrowViewport ? viewportFitHeight || undefined : responsiveHeight

    return (
        <>
            <PageTitle>{title}</PageTitle>
            <PermissionRequired name="access_analytics" accountSlug={accountSlug}>
                {countryCode === 'FR' && (
                    <LanguageSelector
                        options={[
                            { name: 'English', value: 'en-gb' },
                            { name: 'French', value: 'fr' },
                        ]}
                        onChange={() => {
                            fetchEmbedUrl(getUrlParams())
                        }}
                    />
                )}
                {embedUrl && (
                    <iframe
                        className={`sigma-iframe${narrowViewport ? ' narrow' : ''}`}
                        ref={iframeRef}
                        style={{ height: iframeHeight }}
                        src={embedUrl}
                    />
                )}
            </PermissionRequired>
        </>
    )
}
